import type { InvoiceOffer } from '@klear/invoice-offer/invoice-offer'
import { useCallback, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import TableAgGridBulkSelection from '@klear/ui/components/table-ag-grid-bulk-selection'
import { trpc } from '@klear/ui/lib/trpc'
import { Loader2 } from 'lucide-react'
import type { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import { useKlearAgGrid } from '@klear/ui/hooks/use-klear-ag-grid'
import isFinite from 'lodash/isFinite'
import noop from 'lodash/noop'
import { DEFAULT_CURRENCY_CODE } from '@klear/constants/currency'
import { Money, currenciesFromMonies } from '@klear/money'
import { invoiceOfferFunderOrganizationId } from '@klear/invoice-offer/list-display'
import type { KlearFinancialAccount } from '@klear/klear-financial-account/klear-financial-account'
import { klearAccountTypeLabel } from '@klear/klear-financial-account/constants'
import type { WizardStepComponentProps } from '@klear/ui/wizard/flow'
import type { DisbursementStepCtx } from '@/app/disbursements/outlet-context'

const TREASURY_CURRENCY = DEFAULT_CURRENCY_CODE

function funderOrganizationId(row: InvoiceOffer): number | null {
  return invoiceOfferFunderOrganizationId(row.paymentMeta)
}

export default function SelectItemsStep({ ctx }: WizardStepComponentProps<DisbursementStepCtx>) {
  const { state, setState, disbursableAdvances, isDisbursableLoading } = ctx
  const agGrid = useKlearAgGrid()

  const spvOrganizationIds = useMemo(() => {
    const set = new Set<number>()
    for (const row of disbursableAdvances) {
      const id = funderOrganizationId(row)
      if (id != null) set.add(id)
    }
    return [...set].sort((a, b) => a - b)
  }, [disbursableAdvances])

  const { data: spvBalances, isLoading: isSpvBalancesLoading } =
    trpc.klearAccount.listTreasuryBalancesForOrganizations.useQuery(
      { organizationIds: spvOrganizationIds, refreshFromStripe: true },
      { enabled: !isDisbursableLoading && spvOrganizationIds.length > 0 }
    )

  const klearAccountsByOrgId = useMemo(() => {
    const m = new Map<number, KlearFinancialAccount[]>()
    for (const account of spvBalances ?? []) {
      const orgId = account.organization.id
      const existing = m.get(orgId) ?? []
      existing.push(account)
      m.set(orgId, existing)
    }
    return m
  }, [spvBalances])

  const orgLabelById = useMemo(() => {
    const m = new Map<number, string>()
    for (const row of disbursableAdvances) {
      const orgId = funderOrganizationId(row)
      const label = row.fundingSpvLabel?.trim()
      if (orgId != null && label) m.set(orgId, label)
    }
    for (const [orgId, accounts] of klearAccountsByOrgId) {
      const name = accounts[0]?.organization?.name?.trim()
      if (name) m.set(orgId, name)
    }
    return m
  }, [disbursableAdvances, klearAccountsByOrgId])

  const totalAvailableByOrgId = useMemo(() => {
    const m = new Map<number, Money>()
    for (const [orgId, accounts] of klearAccountsByOrgId) {
      if (accounts.length === 0) continue
      m.set(orgId, Money.sum(accounts.flatMap((account) => (account.account ? account.balanceAvailable : []))))
    }
    return m
  }, [klearAccountsByOrgId])

  const disbursableIdsKey = useMemo(() => disbursableAdvances.map((a) => a.id).join(','), [disbursableAdvances])
  useEffect(() => {
    if (disbursableIdsKey.length === 0) return
    setState((s) => {
      if (s.selectedAdvanceIds.length > 0) return s
      const ids = disbursableIdsKey
        .split(',')
        .map((id) => parseInt(id, 10))
        .filter((n) => isFinite(n) && n > 0)
      return { ...s, selectedAdvanceIds: ids }
    })
  }, [disbursableIdsKey, setState])

  const selectedSet = useMemo(() => new Set(state.selectedAdvanceIds), [state.selectedAdvanceIds])

  const isSelected = useCallback((row: InvoiceOffer) => selectedSet.has(row.id), [selectedSet])

  const handleSelected = useCallback(
    (row: InvoiceOffer, isChecked: boolean) => {
      setState((s) => {
        const exists = s.selectedAdvanceIds.includes(row.id)
        if (isChecked && !exists) return { ...s, selectedAdvanceIds: [...s.selectedAdvanceIds, row.id] }
        if (!isChecked && exists)
          return { ...s, selectedAdvanceIds: s.selectedAdvanceIds.filter((id) => id !== row.id) }
        return s
      })
    },
    [setState]
  )

  const outgoingBySpvOrgId = useMemo(() => {
    const pricesByOrg = new Map<number, Money[]>()
    for (const id of state.selectedAdvanceIds) {
      const row = disbursableAdvances.find((a) => a.id === id)
      if (!row) continue
      const orgId = funderOrganizationId(row)
      if (orgId == null) continue
      const list = pricesByOrg.get(orgId) ?? []
      list.push(row.purchasePrice)
      pricesByOrg.set(orgId, list)
    }
    const m = new Map<number, { outgoing: Money; canProjectAgainstTreasury: boolean }>()
    for (const [orgId, prices] of pricesByOrg) {
      const currencies = currenciesFromMonies(prices)
      const canProjectAgainstTreasury =
        currencies.length === 0 || (currencies.length === 1 && currencies[0] === TREASURY_CURRENCY)
      m.set(orgId, { outgoing: Money.sum(prices), canProjectAgainstTreasury })
    }
    return m
  }, [state.selectedAdvanceIds, disbursableAdvances])

  const columnDefs = useMemo<ColDef<InvoiceOffer>[]>(
    () => [
      {
        headerName: 'Funding SPV',
        colId: 'spvOrg',
        field: 'fundingSpvLabel',
        valueFormatter: (p: ValueFormatterParams<InvoiceOffer>) => p.data?.fundingSpvLabel?.trim() || '—',
        filter: 'agTextColumnFilter',
        flex: 1,
        minWidth: 160
      },
      {
        headerName: 'Offer',
        colId: 'idDisplay',
        valueGetter: (p: ValueGetterParams<InvoiceOffer>) => p.data?.idDisplay ?? (p.data ? `#${p.data.id}` : ''),
        filter: 'agTextColumnFilter',
        flex: 1
      },
      {
        headerName: 'Recovery date',
        colId: 'endDate',
        field: 'endDate',
        flex: 1
      },
      {
        headerName: 'Amount to fund',
        colId: 'amount',
        field: 'purchasePrice',
        type: 'rightAligned',
        flex: 1
      }
    ],
    []
  )

  const selectedPurchasePrices = useMemo(
    () => disbursableAdvances.filter((a) => selectedSet.has(a.id)).map((a) => a.purchasePrice),
    [disbursableAdvances, selectedSet]
  )
  const totalToDisburse = useMemo(() => Money.sum(selectedPurchasePrices), [selectedPurchasePrices])

  return (
    <div className="space-y-4 w-full">
      <p className="text-sm text-muted-foreground">
        Select the advances to disburse for this trader. Each offer&apos;s funding SPV is taken from the offer&apos;s
        payment details (invoice offer payment metadata). SPV Klear treasury balances are in {TREASURY_CURRENCY}.
        Projected available subtracts selected {TREASURY_CURRENCY} advances for that SPV; non-
        {TREASURY_CURRENCY} selections are not projected against the treasury balance.
      </p>
      {spvOrganizationIds.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Funder (SPV) Klear treasury balances</p>
          {isSpvBalancesLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading SPV balances…
            </div>
          ) : (
            <div className="space-y-4">
              {spvOrganizationIds.map((orgId) => {
                const accounts = klearAccountsByOrgId.get(orgId) ?? []
                const orgLabel = orgLabelById.get(orgId) || `Org #${orgId}`
                const totalAvailable = totalAvailableByOrgId.get(orgId) ?? Money.zero(TREASURY_CURRENCY)
                const outgoingEntry = outgoingBySpvOrgId.get(orgId)
                const outgoing = outgoingEntry?.outgoing ?? Money.zero(TREASURY_CURRENCY)
                const projected =
                  (outgoingEntry?.canProjectAgainstTreasury ?? true)
                    ? totalAvailable.subtract(
                        outgoing.isPositive() && outgoing.currency === TREASURY_CURRENCY
                          ? outgoing
                          : Money.zero(TREASURY_CURRENCY)
                      )
                    : null

                return (
                  <div key={orgId} className="space-y-2">
                    <p className="text-sm font-medium">{orgLabel}</p>
                    {accounts.length === 0 ? (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base leading-tight">No Klear treasury account</CardTitle>
                          <p className="text-xs text-muted-foreground font-normal">
                            No Klear treasury account on file.
                          </p>
                        </CardHeader>
                      </Card>
                    ) : (
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {accounts.map((account) => (
                          <Card key={account.id}>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-base leading-tight">
                                {klearAccountTypeLabel(account.accountType)}
                              </CardTitle>
                              <p className="text-xs text-muted-foreground font-normal">{account.nameAndLast4}</p>
                            </CardHeader>
                            <CardContent className="text-sm">
                              <div className="flex justify-between gap-2">
                                <span className="text-muted-foreground">Available</span>
                                <span className="font-medium tabular-nums">
                                  {account.account ? account.balanceAvailable.toString() : '—'}
                                </span>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base leading-tight">Total across accounts</CardTitle>
                            <p className="text-xs text-muted-foreground font-normal">
                              Sum of available balances for disbursement projection
                            </p>
                          </CardHeader>
                          <CardContent className="space-y-2 text-sm">
                            <div className="flex justify-between gap-2">
                              <span className="text-muted-foreground">Current available</span>
                              <span className="font-medium tabular-nums">{totalAvailable.toString()}</span>
                            </div>
                            <div className="flex justify-between gap-2">
                              <span className="text-muted-foreground">Selected disbursements</span>
                              <span className="font-medium tabular-nums text-amber-700 dark:text-amber-500">
                                {outgoing.isPositive() ? `−${outgoing.toString()}` : outgoing.toString()}
                              </span>
                            </div>
                            <div className="flex justify-between gap-2 border-t pt-2">
                              <span className="text-muted-foreground">Balance after disbursements</span>
                              <span className="font-semibold tabular-nums">
                                {projected != null ? projected.toString() : '—'}
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
      <div className="flex items-center justify-end">
        {!isDisbursableLoading && (
          <span className="text-sm text-muted-foreground">
            {state.selectedAdvanceIds.length} of {disbursableAdvances.length} selected
          </span>
        )}
      </div>
      {isDisbursableLoading && (
        <div className="flex items-center justify-center gap-2 h-[420px] text-sm text-muted-foreground border rounded-md">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading disbursable advances…
        </div>
      )}
      {!isDisbursableLoading && disbursableAdvances.length === 0 && (
        <div className="flex items-center justify-center h-[420px] text-sm text-muted-foreground border rounded-md">
          No disbursable advances for this trader.
        </div>
      )}
      {!isDisbursableLoading && disbursableAdvances.length > 0 && (
        <>
          <TableAgGridBulkSelection<InvoiceOffer>
            isBulkUpdating={true}
            rowData={disbursableAdvances}
            columnDefs={columnDefs}
            bulkItemSelected={isSelected}
            onBulkItemSelected={handleSelected}
            rowHeight={40}
            headerHeight={40}
            animateRows={false}
            enableCellTextSelection={false}
            pagination={false}
            paginationPageSize={20}
            theme={agGrid.theme}
            onGridReady={noop}
            quickFilterText=""
            defaultColDef={agGrid.defaultColDef}
            columnTypes={agGrid.columnTypes}
            dataTypeDefinitions={agGrid.dataTypeDefinitions}
            className="w-full h-[420px]"
          />
          <div className="flex justify-between text-sm font-medium border-t pt-2">
            <span>Total to disburse</span>
            <span>{totalToDisburse.toString()}</span>
          </div>
        </>
      )}
    </div>
  )
}
